import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';

import Dialog from '~/components/Dialog';
import Button from '~/components/Button';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import LinearProgress from '~/components/LinearProgress';
import Request from '~/providers/Request';
import maskCpf from '~/utils/maskCpf';
import maskCellPhone from '~/utils/maskCellPhone';

const EMPTY = {
  cpf: '',
  cell_phone: '',
  born_date: '',
  email: '',
  gender: '',
  social_name: '',
  postal_code: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city_name: '',
  state_uf: '',
};

const GENDER_OPTIONS = [
  { value: '', label: '' },
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Feminino' },
];

// O input nativo type="date" aceita anos com mais de 4 dígitos ao digitar
// manualmente; limita o ano a 4 dígitos mantendo o formato YYYY-MM-DD.
const clampDateYear = (value) => {
  if (!value) return value;
  const [year, month, day] = value.split('-');
  if (year && year.length > 4) {
    return [year.slice(0, 4), month, day].join('-');
  }
  return value;
};

// Máscaras/limites por campo. CPF e celular só dígitos (limitados a 11) e
// exibidos formatados.
// ATENÇÃO: as máscaras são APENAS de exibição. O que vai para a API precisa ser
// cru (só dígitos) — é assim que cpf/cell_phone/postal_code são gravados em todo
// o sistema (onlyNumbers em UsersController, CorporationImportCpfsService etc.).
// Enviar o valor mascarado gravava "123.456.789-00" no cadastro e quebrava as
// buscas/uniqueness por CPF. Use unmask() em tudo que for enviado.
const unmask = (v) => String(v || '').replace(/\D/g, '');

const maskCep = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

const maskUf = (v) => (v || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);

const FIELD_MASKS = {
  cpf: (v) => maskCpf((v || '').replace(/\D/g, '').slice(0, 11)),
  cell_phone: (v) => maskCellPhone((v || '').replace(/\D/g, '').slice(0, 11)),
  born_date: (v) => clampDateYear(v),
  postal_code: (v) => maskCep(v),
  state_uf: (v) => maskUf(v),
};

const SECTION_STYLE = {
  marginTop: 12,
  marginBottom: -2,
  color: '#37474f',
  fontWeight: 600,
};

// Passo de confirmação do paciente antes de emitir a receita Mevo.
// Busca os dados atuais, exibe tudo pré-preenchido e editável, e o médico
// completa/corrige. As alterações são salvas no cadastro antes da emissão.
function MevoPatientConfirmModal({
  isOpen,
  idClient,
  evolution,
  handleClose,
  onConfirmed,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [fields, setFields] = useState(EMPTY);
  const [patientName, setPatientName] = useState('');
  const lastCepRef = useRef('');
  const numberRef = useRef(null);

  // Carrega os dados do paciente ao abrir o modal.
  useEffect(() => {
    let active = true;
    async function loadPatient() {
      if (!isOpen || !idClient) return;
      setLoading(true);
      lastCepRef.current = '';
      try {
        const resp = await Request.get(
          `/admin/mevo/pacientes/${idClient}/contato`,
          {},
          { normalizeJSON: false }
        );
        const born = resp && resp.born_date ? String(resp.born_date).slice(0, 10) : '';
        const address = (resp && resp.address) || {};
        const next = {
          cpf: FIELD_MASKS.cpf((resp && resp.cpf) || ''),
          cell_phone: FIELD_MASKS.cell_phone((resp && resp.cell_phone) || ''),
          born_date: born,
          email: (resp && resp.email) || '',
          gender: (resp && resp.gender) || '',
          social_name: (resp && resp.social_name) || '',
          postal_code: FIELD_MASKS.postal_code(address.postal_code || ''),
          street: address.street || '',
          number: address.number || '',
          complement: address.complement || '',
          neighborhood: address.neighborhood || '',
          city_name: address.city_name || '',
          state_uf: FIELD_MASKS.state_uf(address.state_uf || ''),
        };
        if (active) {
          setPatientName((resp && resp.name) || '');
          setFields(next);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadPatient();
    return () => {
      active = false;
    };
  }, [isOpen, idClient]);

  // Busca o endereço no ViaCEP, preenche logradouro/bairro/cidade/UF e leva o
  // foco para o Número (único campo que o CEP não resolve). Só sobrescreve o
  // campo quando o ViaCEP retorna valor (CEP único de cidade vem sem logradouro).
  async function fetchCep(cep) {
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await r.json();
      if (data && !data.erro) {
        setFields((f) => ({
          ...f,
          street: data.logradouro || f.street,
          neighborhood: data.bairro || f.neighborhood,
          city_name: data.localidade || f.city_name,
          state_uf: data.uf || f.state_uf,
        }));
        setTimeout(() => {
          if (numberRef.current) numberRef.current.focus();
        }, 80);
      } else {
        toast.info('CEP não encontrado — preencha o endereço manualmente.');
      }
    } catch (e) {
      // Falha de rede/CORS não bloqueia: preenchimento manual continua valendo.
    } finally {
      setCepLoading(false);
    }
  }

  const handleField = (name) => (e) => {
    const { value } = e.target;
    const mask = FIELD_MASKS[name];
    const masked = mask ? mask(value) : value;
    setFields((f) => ({ ...f, [name]: masked }));
    // Ao completar 8 dígitos no CEP, busca o endereço automaticamente (uma vez).
    if (name === 'postal_code') {
      const digits = (masked || '').replace(/\D/g, '');
      if (digits.length === 8 && digits !== lastCepRef.current) {
        lastCepRef.current = digits;
        fetchCep(digits);
      }
    }
  };

  async function handleConfirm() {
    if (!fields.cpf || !fields.cell_phone || !fields.born_date) {
      toast.error('Preencha CPF, celular e data de nascimento do paciente.');
      return;
    }
    // O cadastro é salvo ANTES de chamar a Mevo; sem esta checagem um CPF pela
    // metade era gravado no paciente e só depois a emissão falhava na validação.
    if (unmask(fields.cpf).length !== 11) {
      toast.error('CPF incompleto — informe os 11 dígitos.');
      return;
    }
    if (!fields.gender) {
      toast.error('Selecione o sexo do paciente.');
      return;
    }
    if (!fields.city_name) {
      toast.error('Informe a cidade do paciente (preencha o CEP para completar o endereço).');
      return;
    }

    setLoading(true);
    try {
      // Salva SEMPRE o formulário inteiro (não um "diff") antes de chamar a Mevo,
      // garantindo que o cadastro reflita exatamente o que está na tela.
      await Request.put(
        `/admin/mevo/pacientes/${idClient}/contato`,
        {
          cpf: unmask(fields.cpf),
          cell_phone: unmask(fields.cell_phone),
          born_date: fields.born_date,
          email: fields.email,
          gender: fields.gender,
          social_name: fields.social_name,
          address: {
            postal_code: unmask(fields.postal_code),
            street: fields.street,
            number: fields.number,
            complement: fields.complement,
            neighborhood: fields.neighborhood,
            city_name: fields.city_name,
            state_uf: fields.state_uf,
          },
        },
        {},
        { normalizeJSON: false }
      );

      const resp = await Request.post(
        '/admin/mevo/prescricoes',
        { medical_record_evolution_id: evolution.id, tipo_consulta: 'Presencial' },
        {},
        { normalizeJSON: false }
      );

      setLoading(false);

      if (resp && resp.modal_url) {
        onConfirmed(resp);
      } else {
        toast.error('A Mevo não retornou a URL de emissão.');
      }
    } catch (e) {
      // Erros de validação (422) da Mevo já são exibidos como toast pelo Request.
      setLoading(false);
    }
  }

  // Campo único, sempre editável e pré-preenchido.
  const renderInput = (name, label, opts = {}) => {
    const {
      type = 'text',
      inputProps = {},
      select = false,
      options = [],
      xs = 12,
      required = false,
      helperText,
      endAdornment,
      inputRef,
    } = opts;
    return (
      <GridItem xs={xs}>
        <TextField
          label={label}
          type={type}
          select={select}
          required={required}
          fullWidth
          value={fields[name] || ''}
          onChange={handleField(name)}
          inputProps={inputProps}
          inputRef={inputRef}
          helperText={helperText}
          InputProps={endAdornment ? { endAdornment } : undefined}
          InputLabelProps={type === 'date' ? { shrink: true } : undefined}
          SelectProps={select ? { native: true } : undefined}
        >
          {select &&
            options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
        </TextField>
      </GridItem>
    );
  };

  const sectionTitle = (text) => (
    <GridItem xs={12}>
      <Typography variant="subtitle2" style={SECTION_STYLE}>
        {text}
      </Typography>
    </GridItem>
  );

  return (
    <Dialog
      icon={<LocalHospitalIcon />}
      isOpen={isOpen}
      handleClose={handleClose}
      title="Confirmar dados do paciente"
      maxWidth="md"
      dialogActions={
        <>
          <Button color="grey" disabled={loading} onClick={handleClose}>
            {t('action.cancel')}
          </Button>
          <Button color="green" disabled={loading} onClick={handleConfirm}>
            Confirmar e emitir
          </Button>
        </>
      }
    >
      <LinearProgress loading={loading || cepLoading} />

      {patientName ? (
        <Typography variant="h6" style={{ marginBottom: 2 }}>
          {patientName}
        </Typography>
      ) : null}
      <Typography variant="body2" style={{ marginBottom: 8, color: '#555' }}>
        Confira e complete os dados exigidos pela Mevo. As alterações são salvas
        no cadastro do paciente.
      </Typography>

      <GridContainer>
        {sectionTitle('Identificação')}
        {renderInput('cpf', 'CPF', {
          xs: 6,
          required: true,
          inputProps: { maxLength: 14, inputMode: 'numeric' },
        })}
        {renderInput('born_date', 'Data de nascimento', {
          xs: 6,
          type: 'date',
          required: true,
          inputProps: { max: '9999-12-31', min: '1900-01-01' },
        })}
        {renderInput('gender', 'Sexo', {
          xs: 6,
          select: true,
          options: GENDER_OPTIONS,
          required: true,
        })}
        {renderInput('social_name', 'Nome social', { xs: 6 })}

        {sectionTitle('Contato')}
        {renderInput('cell_phone', 'Celular (DDD + número)', {
          xs: 6,
          required: true,
          inputProps: { maxLength: 15, inputMode: 'numeric' },
        })}
        {renderInput('email', 'E-mail', { xs: 6, type: 'email' })}

        {sectionTitle('Endereço')}
        {renderInput('postal_code', 'CEP', {
          xs: 6,
          helperText: 'Preenche o endereço automaticamente',
          inputProps: { maxLength: 9, inputMode: 'numeric' },
          endAdornment: cepLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={18} />
            </InputAdornment>
          ) : undefined,
        })}
        {renderInput('street', 'Logradouro', { xs: 8 })}
        {renderInput('number', 'Número', { xs: 4, inputRef: numberRef })}
        {renderInput('complement', 'Complemento', { xs: 12 })}
        {renderInput('neighborhood', 'Bairro', { xs: 12 })}
        {renderInput('city_name', 'Cidade', { xs: 8, required: true })}
        {renderInput('state_uf', 'UF', { xs: 4, inputProps: { maxLength: 2 } })}
      </GridContainer>

      <LinearProgress loading={loading} />
    </Dialog>
  );
}

export default MevoPatientConfirmModal;
